import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/hooks/useMasters";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { rupiah, numberID } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Shirt, Pencil, Wand2, Trash2, AlertTriangle } from "lucide-react";

export default function ProductVariants() {
  const { can } = useAuth();
  const { masters, loading: mLoading } = useMasters();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ fabric_type_id: "", age_category_id: "", sleeve_type_id: "", color_id: "", search: "" });
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [delRow, setDelRow] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    if (!showInactive) params.status = "active";
    api.get("/products/variants", { params }).then((r) => setRows(r.data.items)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, [filters, showInactive]);

  const adultSizes = masters.sizes.filter((s) => s.size_category_code === "ADULT");

  return (
    <div>
      <PageHeader title="Varian Produk" subtitle="SKU global — independen dari toko & desain. Format: Bahan-Umur-Lengan-Warna-Ukuran" testId="products-header">
        {can("products.create") && (
          <Button onClick={() => setOpen(true)} data-testid="create-variant-btn" className="bg-indigo-600 hover:bg-indigo-500">
            <Plus className="mr-1.5 h-4 w-4" /> Buat Varian
          </Button>
        )}
      </PageHeader>

      {/* Filters */}
      <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-3 lg:grid-cols-5">
        <div className="relative col-span-2 sm:col-span-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Cari SKU..." className="pl-8" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="product-search" />
        </div>
        <FilterSelect label="Bahan" value={filters.fabric_type_id} onChange={(v) => setFilters({ ...filters, fabric_type_id: v })} options={masters.fabric_types} testId="filter-fabric" />
        <FilterSelect label="Umur" value={filters.age_category_id} onChange={(v) => setFilters({ ...filters, age_category_id: v })} options={masters.age_categories} testId="filter-age" />
        <FilterSelect label="Lengan" value={filters.sleeve_type_id} onChange={(v) => setFilters({ ...filters, sleeve_type_id: v })} options={masters.sleeve_types} testId="filter-sleeve" />
        <FilterSelect label="Warna" value={filters.color_id} onChange={(v) => setFilters({ ...filters, color_id: v })} options={masters.colors} testId="filter-color" />
        <label className="col-span-2 flex items-center gap-2 text-sm text-slate-600 sm:col-span-1" data-testid="toggle-show-inactive">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-indigo-600" />
          Tampilkan Nonaktif
        </label>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading || mLoading ? <Loading /> : rows.length === 0 ? (
          <EmptyState title="Belum ada varian produk" subtitle="Buat varian pertama Anda untuk mulai mengelola SKU global." icon={Shirt} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Bahan</TableHead>
                  <TableHead>Umur</TableHead>
                  <TableHead>Lengan</TableHead>
                  <TableHead>Warna</TableHead>
                  <TableHead>Ukuran</TableHead>
                  <TableHead className="text-right">HPP</TableHead>
                  <TableHead className="text-right">Harga Jual</TableHead>
                  <TableHead className="text-right">Min</TableHead>
                  <TableHead className="text-right">Tersedia</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((v) => (
                  <TableRow key={v.id} data-testid={`variant-row-${v.sku}`}>
                    <TableCell className="font-mono text-xs font-semibold text-indigo-700">{v.sku}</TableCell>
                    <TableCell>{v.fabric_name}</TableCell>
                    <TableCell>{v.age_name}</TableCell>
                    <TableCell>{v.sleeve_name}</TableCell>
                    <TableCell><span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border border-slate-300" style={{ background: v.color_hex }} />{v.color_name}</span></TableCell>
                    <TableCell>{v.size_name}</TableCell>
                    <TableCell className="text-right">{rupiah(v.hpp)}</TableCell>
                    <TableCell className="text-right">{rupiah(v.default_selling_price)}</TableCell>
                    <TableCell className="text-right">{numberID(v.minimum_stock)}</TableCell>
                    <TableCell className="text-right font-semibold">{numberID(v.available)}</TableCell>
                    <TableCell><StatusBadge status={v.status} label={v.status === "active" ? "Aktif" : "Nonaktif"} /></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {can("products.update") && (
                          <Button variant="ghost" size="sm" onClick={() => setEditRow(v)} data-testid={`edit-variant-${v.sku}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {can("product.variant.delete") && (
                          <Button variant="ghost" size="sm" onClick={() => setDelRow(v)} data-testid={`delete-variant-${v.sku}`} title="Hapus / Nonaktifkan">
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {open && <CreateVariantDialog masters={masters} adultSizes={adultSizes} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {editRow && <EditVariantDialog row={editRow} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); load(); }} />}
      {delRow && <DeleteVariantDialog row={delRow} onClose={() => setDelRow(null)} onSaved={() => { setDelRow(null); load(); }} />}
    </div>
  );
}

function DeleteVariantDialog({ row, onClose, onSaved }) {
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/products/variants/${row.id}/delete-check`).then((r) => setCheck(r.data)).catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [row.id]);
  const doDelete = async () => {
    setBusy(true);
    try { const r = await api.delete(`/products/variants/${row.id}`); toast.success(`SKU ${r.data.deleted_sku} dihapus permanen`); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const deactivate = async () => {
    setBusy(true);
    try { await api.put(`/products/variants/${row.id}`, { status: "inactive" }); toast.success(`SKU ${row.sku} dinonaktifkan`); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="delete-variant-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Trash2 className="h-5 w-5 text-rose-600" /> Hapus Varian <span className="font-mono text-indigo-700">{row.sku}</span></DialogTitle>
          <DialogDescription>Hard delete hanya diizinkan jika SKU belum pernah dipakai dalam transaksi apa pun.</DialogDescription>
        </DialogHeader>
        {!check ? <Loading /> : check.deletable ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 text-sm text-rose-800" data-testid="delete-variant-allowed">
            <p>SKU <b className="font-mono">{check.sku}</b> belum pernah digunakan. Tindakan ini <b>permanen</b> dan tidak dapat dibatalkan.</p>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-800" data-testid="delete-variant-blocked">
            <p className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" /> SKU tidak dapat dihapus karena:</p>
            <ul className="ml-5 mt-1 list-disc space-y-0.5">{check.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
            <p className="mt-2">Nonaktifkan SKU sebagai gantinya agar riwayat transaksi tetap utuh.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          {check && !check.deletable && <Button onClick={deactivate} disabled={busy} data-testid="deactivate-variant-btn" className="bg-amber-600 hover:bg-amber-500">Nonaktifkan</Button>}
          {check && check.deletable && <Button onClick={doDelete} disabled={busy} data-testid="confirm-delete-variant-btn" className="bg-rose-600 hover:bg-rose-500">{busy ? "Menghapus..." : "Hapus Permanen"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Semua {label}</SelectItem>
        {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function CreateVariantDialog({ masters, onClose, onSaved }) {
  const [form, setForm] = useState({ fabric_type_id: "", age_category_id: "", sleeve_type_id: "", color_id: "", size_id: "", hpp: 0, default_selling_price: 0, minimum_stock: 0 });
  const [sku, setSku] = useState("");
  const [skuExists, setSkuExists] = useState(false);
  const [saving, setSaving] = useState(false);

  const ageCode = masters.age_categories.find((a) => a.id === form.age_category_id)?.code;
  const sizeOptions = masters.sizes.filter((s) => s.size_category_code === ageCode);

  useEffect(() => {
    const { fabric_type_id, age_category_id, sleeve_type_id, color_id, size_id } = form;
    if (fabric_type_id && age_category_id && sleeve_type_id && color_id && size_id) {
      api.post("/products/generate-sku", { fabric_type_id, age_category_id, sleeve_type_id, color_id, size_id })
        .then((r) => { setSku(r.data.sku); setSkuExists(r.data.exists); }).catch(() => setSku(""));
    } else setSku("");
  }, [form.fabric_type_id, form.age_category_id, form.sleeve_type_id, form.color_id, form.size_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/products/variants", { ...form, hpp: Number(form.hpp), default_selling_price: Number(form.default_selling_price), minimum_stock: Number(form.minimum_stock) });
      toast.success(`Varian ${sku} dibuat`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const valid = form.fabric_type_id && form.age_category_id && form.sleeve_type_id && form.color_id && form.size_id && !skuExists;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Wand2 className="h-5 w-5 text-indigo-600" /> Buat Varian Produk</DialogTitle>
          <DialogDescription>SKU dibuat otomatis dari kombinasi atribut. Toko & desain tidak menjadi bagian dari SKU.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Jenis Bahan"><Sel value={form.fabric_type_id} onChange={(v) => setForm({ ...form, fabric_type_id: v })} options={masters.fabric_types} testId="v-fabric" /></Field>
          <Field label="Kategori Umur"><Sel value={form.age_category_id} onChange={(v) => setForm({ ...form, age_category_id: v, size_id: "" })} options={masters.age_categories} testId="v-age" /></Field>
          <Field label="Jenis Lengan"><Sel value={form.sleeve_type_id} onChange={(v) => setForm({ ...form, sleeve_type_id: v })} options={masters.sleeve_types} testId="v-sleeve" /></Field>
          <Field label="Warna"><Sel value={form.color_id} onChange={(v) => setForm({ ...form, color_id: v })} options={masters.colors} testId="v-color" /></Field>
          <Field label="Ukuran"><Sel value={form.size_id} onChange={(v) => setForm({ ...form, size_id: v })} options={sizeOptions} disabled={!ageCode} testId="v-size" /></Field>
          <Field label="Minimum Stok"><Input type="number" value={form.minimum_stock} onChange={(e) => setForm({ ...form, minimum_stock: e.target.value })} data-testid="v-min" /></Field>
          <Field label="HPP (Rp)"><Input type="number" value={form.hpp} onChange={(e) => setForm({ ...form, hpp: e.target.value })} data-testid="v-hpp" /></Field>
          <Field label="Harga Jual Default (Rp)"><Input type="number" value={form.default_selling_price} onChange={(e) => setForm({ ...form, default_selling_price: e.target.value })} data-testid="v-price" /></Field>
        </div>
        <div className="rounded-lg border border-dashed border-indigo-200 bg-indigo-50/50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">SKU Preview</p>
          <p className="mt-1 font-mono text-lg font-bold text-indigo-700" data-testid="sku-preview">{sku || "—"}</p>
          {skuExists && <p className="mt-1 text-xs text-rose-600">SKU ini sudah ada.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={!valid || saving} data-testid="save-variant-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditVariantDialog({ row, onClose, onSaved }) {
  const [form, setForm] = useState({ hpp: row.hpp, default_selling_price: row.default_selling_price, minimum_stock: row.minimum_stock, status: row.status });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/products/variants/${row.id}`, { hpp: Number(form.hpp), default_selling_price: Number(form.default_selling_price), minimum_stock: Number(form.minimum_stock), status: form.status });
      toast.success("Varian diperbarui"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Ubah Varian <span className="font-mono text-indigo-700">{row.sku}</span></DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="HPP (Rp)"><Input type="number" value={form.hpp} onChange={(e) => setForm({ ...form, hpp: e.target.value })} data-testid="e-hpp" /></Field>
          <Field label="Harga Jual (Rp)"><Input type="number" value={form.default_selling_price} onChange={(e) => setForm({ ...form, default_selling_price: e.target.value })} data-testid="e-price" /></Field>
          <Field label="Minimum Stok"><Input type="number" value={form.minimum_stock} onChange={(e) => setForm({ ...form, minimum_stock: e.target.value })} data-testid="e-min" /></Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="e-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem></SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="update-variant-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>;
}
function Sel({ value, onChange, options, disabled, testId }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Pilih..." /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}
